import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { sortCategoriesByOrder } from "../lib/categoryOrder";
import { PageBackdrop } from "../components/PageBackdrop";

const money = (n) => `S$${Number(n).toLocaleString("en-SG", { maximumFractionDigits: 2 })}`;

const Row = ({ label, price, to, clickable, testid }) => {
    const inner = (
        <>
            <div className="flex items-end gap-3">
                <span className="font-serif-display text-xl sm:text-2xl text-[#3D0C1F] group-hover:text-[#6B1535] transition-colors">{label}</span>
                <span className="flex-1 border-b border-[#6B1535]/35 mb-2 min-w-[24px]" aria-hidden />
                <span className="text-[#6B1535] font-semibold text-base sm:text-lg whitespace-nowrap" data-testid={`${testid}-price`}>{price}</span>
            </div>
        </>
    );
    const cls = "group block py-3";
    return clickable
        ? <Link to={to} className={`${cls} cursor-pointer`} data-testid={testid}>{inner}</Link>
        : <div className={cls} data-testid={testid}>{inner}</div>;
};

const CategoryBlock = ({ category, items, clickable }) => (
    <section className="relative grid md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-6 md:gap-14 py-16 border-b border-[#C29A5B]/25 last:border-b-0" data-testid={`pricing-category-${category}`}>
        <div className="relative md:text-right">
            <div className="font-serif-display text-5xl sm:text-6xl text-[#3D0C1F] leading-[0.9] uppercase tracking-wide">Our<br />Prices</div>
            <div className="font-script text-6xl sm:text-8xl text-[#E27D98]/25 -mt-2 pointer-events-none select-none leading-none whitespace-nowrap md:absolute md:right-0 md:top-32 -z-0" aria-hidden>{category}</div>
            <div className="text-overline mt-4 md:mt-28 relative" data-testid={`pricing-category-title-${category}`}>{category}</div>
        </div>
        <div>
            {items.map((s) => {
                const vs = (s.variants || []).filter((v) => v && v.name);
                const from = vs.length ? Math.min(...vs.map((v) => Number(v.price) || 0)) : s.price;
                return (
                    <div key={s.id}>
                        <Row label={s.name} price={vs.length ? `from ${money(from)}` : money(s.price)}
                            to={`/book?service=${s.id}`} clickable={clickable} testid={`pricing-row-${s.id}`} />
                        {vs.map((v) => (
                            <div key={v.id || v.name} className="pl-5 border-l border-[#C29A5B]/40 ml-1">
                                <Row label={v.name} price={money(v.price)} to={`/book?service=${s.id}${v.id ? `&variant=${v.id}` : ""}`} clickable={clickable} testid={`pricing-variant-${v.id || v.name}`} />
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    </section>
);

export default function PricingPage() {
    const { settings } = useSettings();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    useEffect(() => { api.get("/services").then((r) => setServices(r.data)).catch(() => setError(true)).finally(() => setLoading(false)); }, []);

    const groups = useMemo(() => {
        const g = {};
        services.filter((s) => s.show_on_pricing !== false).forEach((s) => { const c = s.category || "Other"; (g[c] = g[c] || []).push(s); });
        Object.values(g).forEach((arr) => arr.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)));
        return sortCategoriesByOrder(Object.entries(g), settings?.category_order);
    }, [services, settings?.category_order]);

    const clickable = settings?.pricing_rows_clickable !== false;
    const pick = (k, d) => (settings && k in settings ? settings[k] : d);
    const intro = {
        script: pick("pricing_script_word", "Makeover"),
        overline: pick("pricing_overline", "Singapore · The Tresses"),
        heading: pick("pricing_heading", "Pricing at The Tresses"),
        text: pick("pricing_intro", "Transparent prices for every ritual."),
    };

    return (
        <div className="relative isolate min-h-screen" data-testid="pricing-page">
            <PageBackdrop testid="pricing-bg" />
            <div className="relative z-10">
                <header className="pt-40 pb-20 max-w-7xl mx-auto px-6 lg:px-12" data-testid="pricing-hero">
                    {intro.script && <div className="font-script text-5xl sm:text-6xl text-[#B08544]" data-testid="pricing-script-word">{intro.script}</div>}
                    {intro.overline && <div className="text-overline mt-3" data-testid="pricing-overline">{intro.overline}</div>}
                    {intro.heading && <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3D0C1F] mt-4 uppercase tracking-wide" data-testid="pricing-heading">{intro.heading}</h1>}
                    {(intro.text || clickable) && <p className="text-[#7A5262] mt-4 max-w-xl whitespace-pre-line" data-testid="pricing-intro">{[intro.text, clickable && "Tap any service to book it instantly."].filter(Boolean).join(" ")}</p>}
                </header>
                <div className="max-w-6xl mx-auto px-6 lg:px-12 pb-28">
                    <div className="bg-[#FFFAF6]/70 backdrop-blur-[3px] border border-[#C29A5B]/30 px-6 sm:px-12 shadow-[0_30px_80px_-40px_rgba(107,21,53,0.4)]">
                        {loading && <div className="py-20 text-center text-[#7A5262]" data-testid="pricing-loading">Loading prices…</div>}
                        {!loading && error && <div className="py-20 text-center text-[#7A5262]" data-testid="pricing-error">We couldn't load prices right now. Please refresh the page.</div>}
                        {!loading && !error && groups.length === 0 && <div className="py-20 text-center text-[#7A5262]" data-testid="pricing-empty">Prices coming soon.</div>}
                        {groups.map(([cat, items]) => (
                            <CategoryBlock key={cat} category={cat} items={items} clickable={clickable} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
